import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const MarketDataGrid = ({ marketType, symbols, onSymbolSelect }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedSymbols, setSelectedSymbols] = useState(new Set());

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSymbolToggle = (symbol) => {
    const newSelected = new Set(selectedSymbols);
    if (newSelected?.has(symbol)) {
      newSelected?.delete(symbol);
    } else {
      newSelected?.add(symbol);
    }
    setSelectedSymbols(newSelected);
    if (onSymbolSelect) {
      onSymbolSelect(Array.from(newSelected));
    }
  };

  const sortedSymbols = React.useMemo(() => {
    if (!sortConfig?.key) return symbols;
    
    return [...symbols]?.sort((a, b) => {
      if (sortConfig?.key === 'change_percent') {
        const aVal = parseFloat(a?.change_percent);
        const bVal = parseFloat(b?.change_percent);
        return sortConfig?.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      if (sortConfig?.key === 'price') {
        const aVal = parseFloat(a?.price);
        const bVal = parseFloat(b?.price);
        return sortConfig?.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      if (sortConfig?.key === 'volume') {
        const aVal = parseInt(a?.volume);
        const bVal = parseInt(b?.volume);
        return sortConfig?.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      return sortConfig?.direction === 'asc' 
        ? a?.[sortConfig?.key]?.localeCompare(b?.[sortConfig?.key])
        : b?.[sortConfig?.key]?.localeCompare(a?.[sortConfig?.key]);
    });
  }, [symbols, sortConfig]);

  const formatPrice = (price, currency = '₹') => {
    if (marketType === 'crypto') {
      return `$${parseFloat(price)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
    }
    return `${currency}${parseFloat(price)?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatVolume = (volume) => {
    const num = parseInt(volume);
    if (num >= 10000000) return `${(num / 10000000)?.toFixed(1)}Cr`;
    if (num >= 100000) return `${(num / 100000)?.toFixed(1)}L`;
    if (num >= 1000) return `${(num / 1000)?.toFixed(1)}K`;
    return num?.toLocaleString('en-IN');
  };

  const getChangeColor = (change) => {
    const changeVal = parseFloat(change);
    if (changeVal > 0) return 'text-success';
    if (changeVal < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const getChangeIcon = (change) => {
    const changeVal = parseFloat(change);
    if (changeVal > 0) return 'TrendingUp';
    if (changeVal < 0) return 'TrendingDown';
    return 'Minus';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="w-8 px-3 py-3">
                <input
                  type="checkbox"
                  className="rounded border-border"
                  onChange={(e) => {
                    if (e?.target?.checked) {
                      setSelectedSymbols(new Set(symbols.map(s => s.symbol)));
                    } else {
                      setSelectedSymbols(new Set());
                    }
                  }}
                />
              </th>
              <th 
                className="text-left px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted/50 transition-smooth"
                onClick={() => handleSort('symbol')}
              >
                <div className="flex items-center space-x-2">
                  <span>Symbol</span>
                  {sortConfig?.key === 'symbol' && (
                    <Icon 
                      name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="text-right px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted/50 transition-smooth"
                onClick={() => handleSort('price')}
              >
                <div className="flex items-center justify-end space-x-2">
                  <span>Price</span>
                  {sortConfig?.key === 'price' && (
                    <Icon 
                      name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="text-right px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted/50 transition-smooth"
                onClick={() => handleSort('change_percent')}
              >
                <div className="flex items-center justify-end space-x-2">
                  <span>Change %</span>
                  {sortConfig?.key === 'change_percent' && (
                    <Icon 
                      name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="text-right px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted/50 transition-smooth"
                onClick={() => handleSort('volume')}
              >
                <div className="flex items-center justify-end space-x-2">
                  <span>Volume</span>
                  {sortConfig?.key === 'volume' && (
                    <Icon 
                      name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  )}
                </div>
              </th>
              <th className="text-center px-4 py-3 font-medium text-foreground">
                RSI
              </th>
              <th className="text-center px-4 py-3 font-medium text-foreground">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSymbols?.map((item, index) => (
              <tr 
                key={item?.symbol}
                className={`border-b border-border hover:bg-muted/30 transition-smooth ${
                  selectedSymbols?.has(item?.symbol) ? 'bg-primary/5' : ''
                }`}
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    className="rounded border-border"
                    checked={selectedSymbols?.has(item?.symbol)}
                    onChange={() => handleSymbolToggle(item?.symbol)}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-primary">
                        {item?.symbol?.substring(0, 2)}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{item?.symbol}</div>
                      <div className="text-xs text-muted-foreground">{item?.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="font-medium text-foreground">
                    {formatPrice(item?.price)}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className={`flex items-center justify-end space-x-1 ${getChangeColor(item?.change_percent)}`}>
                    <Icon name={getChangeIcon(item?.change_percent)} size={14} />
                    <span className="font-medium">
                      {parseFloat(item?.change_percent) > 0 ? '+' : ''}{parseFloat(item?.change_percent)?.toFixed(2)}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="text-muted-foreground">
                    {formatVolume(item?.volume)}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    item?.rsi > 70 ? 'bg-error/10 text-error' :
                    item?.rsi < 30 ? 'bg-success/10 text-success': 'bg-muted text-muted-foreground'
                  }`}>
                    {item?.rsi?.toFixed(1)}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                    item?.status === 'active' ? 'bg-success/10 text-success' :
                    item?.status === 'paused'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                  }`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${
                      item?.status === 'active' ? 'bg-success' :
                      item?.status === 'paused'? 'bg-warning' : 'bg-error'
                    }`}></div>
                    <span className="capitalize">{item?.status}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selectedSymbols?.size > 0 && (
        <div className="bg-primary/5 border-t border-border px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedSymbols?.size} symbols selected
            </span>
            <div className="flex items-center space-x-2">
              <button className="text-xs text-primary hover:text-primary/80 transition-smooth">
                Add to Watchlist
              </button>
              <button className="text-xs text-primary hover:text-primary/80 transition-smooth">
                Set Alerts
              </button>
              <button className="text-xs text-error hover:text-error/80 transition-smooth">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketDataGrid;